import React, { useState, useEffect } from 'react';
import { X, Save, Plus, Trash2, MessageSquare, Send } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import fipsData from '../../data/fips.json';
import { wsService } from '../../services/WebSocketService';
import { useTaxListUpload } from '../../hooks/useTaxListUpload';

interface County {
  state: string;
  county: string;
}

// Cast fips data to proper type
const typedFipsData = fipsData as Record<string, { STATE: string; COUNTY: string }>;

// Get unique states from FIPS data
const US_STATES = Array.from(new Set(Object.values(typedFipsData).map(record => record.STATE))).sort();

interface DFPBOrderEditModalProps {
  order: {
    orderId: string;
    studentName: string;
    studentEmail: string;
    counties: string[];
    orderType: string;
    quantity: number;
    status: string;
    adminNotes: string;
    downloadLink: string | null;
    submittedDate: string;
    deliveredDate: string | null;
  };
  onClose: () => void;
  onSaved: () => void;
}

export function DFPBOrderEditModal({ order, onClose, onSaved }: DFPBOrderEditModalProps) {
  // Parse order.counties array (strings like "County, State") into County[] objects
  const parseCounties = (): County[] => {
    if (!order.counties || order.counties.length === 0) {
      return [{ state: '', county: '' }];
    }

    return order.counties.map(countyStr => {
      const parts = countyStr.split(',').map(p => p.trim());
      if (parts.length === 2) {
        return { county: parts[0], state: parts[1] };
      }
      return { state: '', county: '' };
    });
  };

  const [status, setStatus] = useState(order.status);
  const [adminNotes, setAdminNotes] = useState(order.adminNotes || '');
  const [downloadLink, setDownloadLink] = useState(order.downloadLink || '');
  const [counties, setCounties] = useState<County[]>(parseCounties());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admin discussion state
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // User groups state
  const [userGroups, setUserGroups] = useState<string[]>([]);

  // Helper function to update county
  const updateCounty = (index: number, field: 'state' | 'county', value: string) => {
    const updated = [...counties];
    updated[index][field] = value;

    // Clear county when state changes
    if (field === 'state') {
      updated[index].county = '';
    }

    setCounties(updated);
  };

  const addCounty = () => {
    if (counties.length < 3) {
      setCounties([...counties, { state: '', county: '' }]);
    }
  };

  const removeCounty = (index: number) => {
    if (counties.length > 1) {
      setCounties(counties.filter((_, i) => i !== index));
    }
  };

  // Get available counties for a given state
  const getCountiesForState = (state: string): string[] => {
    if (!state) return [];
    const countyList = Object.values(typedFipsData)
      .filter((record) => record.STATE === state)
      .map((record) => record.COUNTY)
      .sort((a, b) => a.localeCompare(b));
    return [...new Set(countyList)];
  };

  // Auto-reset status to 'new' if any county area changes
  useEffect(() => {
    const originalCounties = parseCounties();

    // Check if counties have changed
    const countiesChanged =
      counties.length !== originalCounties.length ||
      counties.some((c, i) =>
        c.state !== originalCounties[i]?.state ||
        c.county !== originalCounties[i]?.county
      );

    if (countiesChanged && status !== 'new') {
      setStatus('new');
    }
  }, [counties, status]);

  // Fetch user groups on mount
  useEffect(() => {
    const getUserGroups = async () => {
      try {
        const session = await fetchAuthSession();
        const groups = session.tokens?.accessToken?.payload['cognito:groups'] as string[] || [];
        setUserGroups(groups);
      } catch (err) {
        console.error('Error fetching user groups:', err);
      }
    };
    getUserGroups();
  }, []);

  // Fetch admin discussion messages
  useEffect(() => {
    fetchMessages();
  }, [order.orderId]);

  // WebSocket listener for real-time message updates
  useEffect(() => {
    const handleNotification = (notification: any) => {
      // Refresh messages when DFPB admin note is added
      if (notification.type === 'dfpb_admin_only_note' || notification.type === 'dfpb_message_from_admin') {
        console.log('DFPB message received via WebSocket, refreshing admin discussion');
        fetchMessages();
      }
    };

    wsService.on('notification', handleNotification);

    return () => {
      wsService.off('notification', handleNotification);
    };
  }, [order.orderId]);

  const fetchMessages = async () => {
    try {
      setLoadingMessages(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) return;

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/dfpb-orders/${order.orderId}/messages`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const { uploading: uploadingFile, uploadFile } = useTaxListUpload({
    endpoint: `/admin/dfpb-orders/${order.orderId}/upload-dfpb-list`,
    onSuccess: (result) => {
      setDownloadLink(result.downloadUrl);
      alert(`File uploaded successfully!\n\nFile: ${result.metadata.fileName}\nSize: ${(result.metadata.fileSize / 1024).toFixed(1)} KB\nRecords: ${result.metadata.recordCount}`);
      onSaved();
    },
    onError: (error) => {
      alert(error.message);
    }
  });

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await uploadFile(file);
    } finally {
      // Reset file input
      event.target.value = '';
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMessage.trim()) return;

    try {
      setSendingMessage(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token');
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/dfpb-orders/${order.orderId}/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            message_text: newMessage.trim(),
            isAdminOnly: true
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setNewMessage('');
      await fetchMessages(); // Refresh messages
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  const statusOptions = [
    { value: 'new', label: 'New', color: 'bg-purple-100 text-purple-800' },
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'processing', label: 'Processing', color: 'bg-blue-100 text-blue-800' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
    { value: 'update', label: 'Update', color: 'bg-orange-100 text-orange-800' },
    { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-800' },
    { value: 'need_new_areas', label: 'Need New Areas', color: 'bg-pink-100 text-pink-800' }
  ];

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token');
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/dfpb-orders/${order.orderId}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status,
            admin_notes: adminNotes,
            counties: counties.map(c => `${c.county}, ${c.state}`).filter(c => c.trim() !== ',')
            // download_link excluded - field is disabled until CSV generation is ready
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update order');
      }

      onSaved();
      onClose();
    } catch (err) {
      console.error('Error updating order:', err);
      setError(err instanceof Error ? err.message : 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = (() => {
    const originalCounties = parseCounties();

    const countiesChanged =
      counties.length !== originalCounties.length ||
      counties.some((c, i) =>
        c.state !== originalCounties[i]?.state ||
        c.county !== originalCounties[i]?.county
      );

    return (
      status !== order.status ||
      adminNotes !== (order.adminNotes || '') ||
      countiesChanged
    );
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Edit Order</h2>
            <p className="text-sm text-gray-500 mt-1">{order.studentName} - {order.orderId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Order Details (Read-only) */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-gray-900 mb-3">Order Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Student:</span>
                <span className="ml-2 font-medium text-gray-900">{order.studentName}</span>
              </div>
              <div>
                <span className="text-gray-600">Email:</span>
                <span className="ml-2 font-medium text-gray-900">{order.studentEmail}</span>
              </div>
              <div>
                <span className="text-gray-600">Order Type:</span>
                <span className="ml-2 font-medium text-gray-900">{order.orderType}</span>
              </div>
              <div>
                <span className="text-gray-600">Submitted:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {new Date(order.submittedDate).toLocaleDateString()}
                </span>
              </div>
              {order.deliveredDate && (
                <div>
                  <span className="text-gray-600">Delivered:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {new Date(order.deliveredDate).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* County Areas (Max 3) - Auto-resets status to 'new' when ANY area changes */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                County Areas (Max 3) {(() => {
                  const originalCounties = parseCounties();
                  const countiesChanged =
                    counties.length !== originalCounties.length ||
                    counties.some((c, i) =>
                      c.state !== originalCounties[i]?.state ||
                      c.county !== originalCounties[i]?.county
                    );
                  return countiesChanged && (
                    <span className="text-xs text-orange-600 ml-2">(Status will reset to 'New')</span>
                  );
                })()}
              </label>
              {counties.length < 3 && (
                <button
                  type="button"
                  onClick={addCounty}
                  className="text-tdb-accent hover:text-tdb-accent-dark font-semibold text-sm flex items-center space-x-1"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add County</span>
                </button>
              )}
            </div>

            <div className="space-y-3">
              {counties.map((county, index) => (
                <div key={index} className="space-y-2">
                  <label className="block text-xs font-medium text-gray-600">Area {index + 1}</label>
                  <div className="flex items-center space-x-2">
                    {/* State Dropdown */}
                    <select
                      value={county.state}
                      onChange={(e) => updateCounty(index, 'state', e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tdb-accent focus:border-transparent"
                    >
                      <option value="">State</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>

                    {/* County Dropdown */}
                    <select
                      value={county.county}
                      onChange={(e) => updateCounty(index, 'county', e.target.value)}
                      disabled={!county.state}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tdb-accent focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">Select County</option>
                      {getCountiesForState(county.state).map(countyName => (
                        <option key={countyName} value={countyName}>{countyName}</option>
                      ))}
                    </select>

                    {/* Remove Button */}
                    {counties.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCounty(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tdb-accent focus:border-transparent"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Download Link - Disabled until CSV generation is ready */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Download Link
            </label>
            <div className="relative">
              <input
                type="url"
                value={downloadLink}
                disabled={true}
                placeholder="CSV download will be available here"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-400 cursor-not-allowed"
                data-lpignore="true"
                data-form-type="other"
              />
              {/* TODO: Add Download CSV icon here when data is ready */}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              CSV download functionality coming soon
            </p>
          </div>

          {/* Admin Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admin Notes
            </label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add internal notes about this order..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tdb-accent focus:border-transparent resize-none"
              data-lpignore="true"
              data-form-type="other"
            />
          </div>

          {/* File Upload/Download Section */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Order Data File
            </label>

            {/* Download section - visible to everyone if file exists */}
            {downloadLink && (
              <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 mb-2">File available for download:</p>
                <a
                  href={downloadLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold"
                >
                  Download Data File
                </a>
              </div>
            )}

            {/* Upload section - only visible to taxlatesdata group */}
            {userGroups.includes('taxlatesdata') && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800 mb-2">
                  {downloadLink ? 'Replace data file:' : 'Upload data file:'}
                </p>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-600 file:text-white hover:file:bg-orange-700 file:cursor-pointer disabled:opacity-50"
                />
                {uploadingFile && (
                  <p className="text-sm text-orange-600 mt-2">Uploading file...</p>
                )}
              </div>
            )}
          </div>

          {/* Admin Discussion */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              <label className="block text-sm font-medium text-gray-700">
                Admin Discussion
              </label>
              <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-semibold rounded">
                Internal Only
              </span>
            </div>

            {/* Messages List */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 max-h-60 overflow-y-auto mb-3">
              {loadingMessages ? (
                <div className="p-4 text-center text-gray-500">Loading messages...</div>
              ) : messages.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No messages yet. Start the admin discussion below.
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {messages.map((message: any) => (
                    <div key={message.message_id} className="p-3">
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="font-semibold text-sm text-gray-900">
                          {message.sender_email || message.sender_name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(message.timestamp)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-800 bg-purple-50 border-l-4 border-purple-500 rounded px-3 py-2">
                        {message.message_text}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type admin message..."
                className="flex-1 px-4 py-2 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                disabled={sendingMessage}
                data-lpignore="true"
                data-form-type="other"
              />
              <button
                type="submit"
                disabled={sendingMessage || !newMessage.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                <span>{sendingMessage ? 'Sending...' : 'Send'}</span>
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="px-6 py-2 bg-tdb-accent text-white rounded-lg hover:bg-tdb-accent-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
